import React, { useState } from 'react';
import BentoCard from '../ui/BentoCard';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
}

function ExpiryBadge({ daysLeft, expirationDate }) {
  if (daysLeft === null || daysLeft === undefined) return null;
  const label = formatDate(expirationDate);
  if (daysLeft < 0) return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-error-container text-error">Expired</span>;
  if (daysLeft <= 7)  return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-error-container text-error">{daysLeft}d left</span>;
  if (daysLeft <= 30) return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-warning-light text-warning">{label ? `Exp ${label}` : `${daysLeft}d left`}</span>;
  if (label)          return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-surface-container text-on-surface-variant">Until {label}</span>;
  return null;
}

function getCategoryIcon(category) {
  const c = (category || '').toLowerCase();
  if (c.includes('dining') || c.includes('restaurant') || c.includes('food')) return 'restaurant';
  if (c.includes('travel') || c.includes('flight') || c.includes('airline')) return 'flight';
  if (c.includes('hotel') || c.includes('lodging')) return 'hotel';
  if (c.includes('grocery') || c.includes('supermarket')) return 'local_grocery_store';
  if (c.includes('gas') || c.includes('fuel')) return 'local_gas_station';
  if (c.includes('entertain') || c.includes('streaming')) return 'movie';
  if (c.includes('transit') || c.includes('uber') || c.includes('lyft')) return 'directions_transit';
  if (c.includes('online') || c.includes('shopping')) return 'shopping_bag';
  return 'star';
}

// ─── Alert Banners ───────────────────────────────────────────────────────────

function AlertBanners({ alerts, onDismiss }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="space-y-2">
      {alerts.map((alert, i) => {
        const urgent = alert.daysUntilExpiry <= 7;
        return (
          <div key={i} className={`flex items-start gap-3 p-3.5 rounded-2xl ${urgent ? 'bg-error-container/40 border border-error/10' : 'bg-warning-light border border-warning/10'}`}>
            <span className={`material-symbols-outlined filled text-[20px] mt-0.5 flex-shrink-0 ${urgent ? 'text-error' : 'text-warning'}`}>
              {urgent ? 'error' : 'warning'}
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${urgent ? 'text-error' : 'text-warning'}`}>
                {urgent ? 'Expiring very soon' : 'Expiring soon'}
              </p>
              <p className="text-xs text-on-surface mt-0.5 leading-snug">
                <span className="font-medium">{alert.merchant}</span> offer on your <span className="font-medium">{alert.cardLabel}</span>{' — '}{alert.value}
                {alert.daysUntilExpiry <= 0 ? ' has expired' : ` expires in ${alert.daysUntilExpiry} day${alert.daysUntilExpiry === 1 ? '' : 's'}`}
              </p>
            </div>
            <button onClick={() => onDismiss(i)} className="flex-shrink-0 text-on-surface-variant hover:text-on-surface transition-colors">
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Single Card Benefits Block ───────────────────────────────────────────────

function CardBenefitBlock({ cardBenefits }) {
  const [expanded, setExpanded] = useState(false);
  const { card, generalBenefits, merchantOffers, categoryBonuses, validUntil, lastUpdated } = cardBenefits;

  const merchantEntries = Object.entries(merchantOffers || {});
  const categoryEntries = Object.entries(categoryBonuses || {});
  const hasContent = generalBenefits?.length > 0 || merchantEntries.length > 0 || categoryEntries.length > 0;
  const expiringSoonCount = merchantEntries.filter(([, o]) => o.isExpiringSoon && (o.daysUntilExpiry ?? 999) >= 0).length;

  return (
    <div className="border border-outline-variant/15 rounded-2xl overflow-hidden">
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-white hover:bg-surface-container-low transition-colors text-left"
      >
        <div className="w-9 h-6 rounded-md flex-shrink-0" style={{ background: card.color || '#5856a2' }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-on-surface truncate">{card.cardName || `${card.bank} ${card.network}`}</p>
          {validUntil && <p className="text-[11px] text-on-surface-variant mt-0.5">Valid until {formatDate(validUntil)}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {expiringSoonCount > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-warning-light text-warning">{expiringSoonCount} expiring</span>
          )}
          <span className={`material-symbols-outlined text-on-surface-variant text-[20px] transition-transform ${expanded ? 'rotate-180' : ''}`}>expand_more</span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-3 space-y-4 bg-white">
          {!hasContent && <p className="text-xs text-on-surface-variant text-center py-2">No benefits data available yet.</p>}

          {merchantEntries.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label mb-2">Merchant Offers</p>
              <div className="space-y-2">
                {merchantEntries.map(([merchant, offer]) => (
                  <div key={merchant} className={`p-3 rounded-xl ${offer.isExpiringSoon ? 'bg-warning-light/50 border border-warning/20' : 'bg-surface-container-low'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-on-surface">{merchant}</p>
                        <p className="text-xs text-primary font-medium mt-0.5">{offer.value}</p>
                        {offer.description && <p className="text-[11px] text-on-surface-variant mt-0.5 leading-snug">{offer.description}</p>}
                      </div>
                      <ExpiryBadge daysLeft={offer.daysUntilExpiry} expirationDate={offer.expirationDate} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {categoryEntries.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label mb-2">Bonus Categories</p>
              <div className="grid grid-cols-2 gap-2">
                {categoryEntries.map(([category, multiplier]) => (
                  <div key={category} className="flex items-center gap-2 p-2.5 bg-primary-light/50 rounded-xl">
                    <span className="material-symbols-outlined text-primary text-[16px]">{getCategoryIcon(category)}</span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-primary truncate">{multiplier}</p>
                      <p className="text-[10px] text-on-surface-variant truncate">{category}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {generalBenefits?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label mb-2">General Benefits</p>
              <ul className="space-y-1.5">
                {generalBenefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="material-symbols-outlined filled text-success text-[14px] mt-0.5 flex-shrink-0">check_circle</span>
                    <p className="text-xs text-on-surface leading-snug">{benefit}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lastUpdated && <p className="text-[10px] text-on-surface-variant/60 pt-1">Last refreshed {formatDate(lastUpdated)}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Main Panel ──────────────────────────────────────────────────────────────

function CardBenefitsPanel({ benefitsData, loading, error, onRefresh }) {
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  if (loading) {
    return (
      <BentoCard>
        <div className="flex items-center gap-2 mb-4">
          <span className="material-symbols-outlined text-primary" style={{ fontSize: 22 }}>credit_score</span>
          <h2 className="text-base font-semibold text-on-surface">Your Card Benefits</h2>
        </div>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <div className="w-8 h-8 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-on-surface-variant">Loading your benefits…</p>
        </div>
      </BentoCard>
    );
  }

  if (error) {
    return (
      <BentoCard>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-on-surface">Your Card Benefits</h2>
          <button onClick={onRefresh} className="text-xs font-medium text-primary flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">refresh</span>Retry
          </button>
        </div>
        <p className="text-sm text-on-surface-variant text-center py-4">Could not load benefits. Tap Retry to try again.</p>
      </BentoCard>
    );
  }

  // No data yet and no error — show a quiet tap-to-load state rather than disappearing
  if (!benefitsData || benefitsData.benefits?.length === 0) {
    return (
      <BentoCard>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined filled text-primary" style={{ fontSize: 22 }}>credit_score</span>
            <h2 className="text-base font-semibold text-on-surface">Your Card Benefits</h2>
          </div>
          <button onClick={onRefresh} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-on-surface-variant text-[18px]">refresh</span>
          </button>
        </div>
        <p className="text-sm text-on-surface-variant mt-3 text-center py-2">
          Tap refresh to load your card benefits.
        </p>
      </BentoCard>
    );
  }

  const visibleAlerts = (benefitsData.alerts || []).filter((_, i) => !dismissedAlerts.includes(i));

  return (
    <div className="space-y-3">
      {visibleAlerts.length > 0 && (
        <AlertBanners alerts={visibleAlerts} onDismiss={i => setDismissedAlerts(p => [...p, i])} />
      )}

      <BentoCard className="!p-0 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant/15">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined filled text-primary" style={{ fontSize: 22 }}>credit_score</span>
            <h2 className="text-base font-semibold text-on-surface">Your Card Benefits</h2>
          </div>
          <button onClick={onRefresh} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors" title="Refresh">
            <span className="material-symbols-outlined text-on-surface-variant text-[18px]">refresh</span>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {benefitsData.benefits.map(cb => (
            <CardBenefitBlock key={cb.card.id} cardBenefits={cb} />
          ))}
        </div>

        {benefitsData.timestamp && (
          <p className="text-[10px] text-on-surface-variant/50 text-center pb-3">Updated {formatDate(benefitsData.timestamp)}</p>
        )}
      </BentoCard>
    </div>
  );
}

export default CardBenefitsPanel;
